import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  BookText, Plus, X, FileDown, Trash2, ChefHat, Martini, Search,
  GripVertical, Loader2, Edit3, Tag, Euro, Percent, AlertTriangle,
} from "lucide-react";
import api, { formatApiError, API } from "../api";
import { formatEUR as fmtEur } from "../utils/format";
import {
  Button, Card, Input, Select, Label, Badge, Spinner, EmptyState,
} from "../components/ui/Primitives";
import useAutoSaveDraft from "../hooks/useAutoSaveDraft";
import DraftRestoreBanner from "../components/DraftRestoreBanner";
import useUnsavedChanges from "../hooks/useUnsavedChanges";
import useTouchDraggable from "../hooks/useTouchDraggable";

// ---------- helpers ----------
const fmtPct = (v) => `${Number(v || 0).toFixed(1).replace(".", ",")} %`;
const tone = (t) => (t === "green" ? "ok" : t === "yellow" ? "warn" : t === "red" ? "err" : "default");
const uid = () => Math.random().toString(36).slice(2, 12);

// ---------- KPI ----------
function Kpi({ label, value, sub, tone: t = "default" }) {
  const accent = t === "ok" ? "border-ok/30" : t === "warn" ? "border-warn/30" : t === "err" ? "border-err/30" : "border-white/10";
  return (
    <div className={`bg-bg-surface border ${accent} rounded-xl px-4 py-3`} data-testid="menu-kpi">
      <div className="text-[10px] uppercase tracking-wider text-ink-muted font-medium">{label}</div>
      <div className="num text-xl font-medium text-ink-primary tracking-tight mt-1">{value}</div>
      {sub && <div className="text-[10px] text-ink-muted mt-0.5">{sub}</div>}
    </div>
  );
}

// ---------- Dish Picker ----------
function DishPicker({ open, onClose, onPick }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState("recipe");
  const [search, setSearch] = useState("");
  const [recipes, setRecipes] = useState([]);
  const [cocktails, setCocktails] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [r, c] = await Promise.all([
          api.get("/recipes").catch(() => ({ data: { items: [] } })),
          api.get("/cocktails").catch(() => ({ data: { items: [] } })),
        ]);
        if (!alive) return;
        setRecipes(r.data.items || []);
        setCocktails(c.data.items || []);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open]);

  const items = useMemo(() => {
    const src = tab === "recipe" ? recipes : cocktails;
    const q = search.trim().toLowerCase();
    return src.filter((x) => !q || (x.name || "").toLowerCase().includes(q));
  }, [tab, recipes, cocktails, search]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-bg-void/80 backdrop-blur-sm px-4" onClick={onClose}>
      <div className="bg-bg-surface border border-white/10 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="dish-picker">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <button onClick={() => setTab("recipe")} className={`px-3 py-1.5 rounded-md text-xs font-medium ${tab === "recipe" ? "bg-brand/15 text-brand" : "text-ink-secondary hover:text-ink-primary"}`} data-testid="picker-tab-recipe">
              <ChefHat size={12} className="inline mr-1" /> {t("menus.recipes")}
            </button>
            <button onClick={() => setTab("cocktail")} className={`px-3 py-1.5 rounded-md text-xs font-medium ${tab === "cocktail" ? "bg-brand/15 text-brand" : "text-ink-secondary hover:text-ink-primary"}`} data-testid="picker-tab-cocktail">
              <Martini size={12} className="inline mr-1" /> {t("menus.cocktails")}
            </button>
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink-primary p-1" data-testid="picker-close"><X size={18} /></button>
        </div>
        <div className="p-4 border-b border-white/10">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("menus.search_dish")} className="pl-9" data-testid="picker-search" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {loading ? (
            <div className="flex items-center justify-center py-10"><Spinner size={20} /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-10 text-sm text-ink-muted">{t("menus.no_dishes_found")}</div>
          ) : (
            <div className="grid grid-cols-1 gap-1.5">
              {items.map((d) => (
                <button
                  key={d.id}
                  onClick={() => { onPick({ kind: tab, dish_id: d.id, name: d.name, category: d.category }); }}
                  className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-bg-void border border-white/10 hover:border-brand/40 hover:bg-brand/5 transition-all text-left"
                  data-testid={`picker-item-${d.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-ink-primary font-medium truncate">{d.name}</div>
                    <div className="text-[11px] text-ink-muted">{d.category}</div>
                  </div>
                  <Plus size={14} className="text-brand" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Section card ----------
// Helper: a row inside a Section that supports both HTML5 D&D and touch
function DraggableSectionItem({ it, iIdx, sectionIdx, onItemDragStart, onItemDragOver, onItemDrop, onItemChange, onItemRemove, onItemReorder }) {
  const { t } = useTranslation();
  const touchProps = useTouchDraggable({
    index: iIdx,
    dragType: `item-sec-${sectionIdx}`,
    onReorder: (from, to) => onItemReorder(from, to),
  });
  return (
    <div
      key={it.id}
      className="flex items-center gap-2 px-2.5 py-2 rounded-md bg-bg-void border border-white/10 cursor-move"
      data-testid={`section-item-${it.id}`}
      draggable
      onDragStart={() => onItemDragStart(iIdx)}
      onDragOver={onItemDragOver}
      onDrop={(e) => onItemDrop(e, iIdx)}
      {...touchProps}
    >
      <GripVertical size={11} className="text-ink-muted opacity-50" />
      {it.kind === "recipe" ? <ChefHat size={13} className="text-brand" /> : <Martini size={13} className="text-warn" />}
      <div className="flex-1 min-w-0">
        <div className="text-sm text-ink-primary truncate">{it.name || it.dish?.name || "—"}</div>
        <div className="text-[11px] text-ink-muted">{it.dish?.category || it.category || ""}</div>
      </div>
      <div className="flex items-center gap-1">
        <Input
          type="number"
          step="0.10"
          min="0"
          value={it.menu_price ?? ""}
          onChange={(e) => onItemChange(iIdx, { ...it, menu_price: e.target.value === "" ? null : Number(e.target.value) })}
          placeholder={it.dish?.effective_price ? String(it.dish.effective_price.toFixed(2)) : t("menus.price_ph")}
          className="w-24 text-right"
          data-testid={`item-price-${it.id}`}
        />
      </div>
      <button onClick={() => onItemRemove(iIdx)} className="text-ink-muted hover:text-err p-1" data-testid={`item-remove-${it.id}`}>
        <X size={14} />
      </button>
    </div>
  );
}

// Phase 30 F9 — section types
const SECTION_TYPES = ["Vorspeise", "Suppe", "Hauptgang", "Dessert", "Individuell"];

function SectionCard({ section, idx, sectionsCount, onChange, onRemove, onAddItem, onItemChange, onItemRemove, onItemReorder, onSectionDrag, dragState, setDragState }) {
  const { t } = useTranslation();

  // Item-level HTML5 drag handlers
  const onItemDragStart = (iIdx) => setDragState({ sectionIdx: idx, itemIdx: iIdx });
  const onItemDragOver = (e) => { e.preventDefault(); };
  const onItemDrop = (e, dropIdx) => {
    e.preventDefault();
    if (!dragState || dragState.sectionIdx !== idx) return;
    if (dragState.itemIdx === dropIdx) return;
    onItemReorder(dragState.itemIdx, dropIdx);
    setDragState(null);
  };

  // Phase 31 — touch fallback for section-level reorder
  const sectionTouchProps = useTouchDraggable({
    index: idx,
    dragType: "section",
    onReorder: (from, to) => onSectionDrag("touch-reorder", from, to),
  });

  return (
    <Card
      className="p-4 space-y-3"
      data-testid={`menu-section-${section.id}`}
      draggable
      onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; onSectionDrag("start", idx); }}
      onDragOver={(e) => { e.preventDefault(); }}
      onDrop={(e) => { e.preventDefault(); onSectionDrag("drop", idx); }}
      {...sectionTouchProps}
    >
      <div className="flex items-center gap-2">
        <span className="cursor-move" title={t("menus.drag_section", "Sektion verschieben")}>
          <GripVertical size={14} className="text-ink-muted" />
        </span>
        <Input
          value={section.name}
          onChange={(e) => onChange({ ...section, name: e.target.value })}
          placeholder={t("menus.section_name_ph")}
          className="flex-1"
          data-testid={`section-name-${section.id}`}
        />
        <Select
          value={section.type || "Individuell"}
          onChange={(e) => onChange({ ...section, type: e.target.value })}
          className="w-36"
          data-testid={`section-type-${section.id}`}
        >
          {SECTION_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
        </Select>
        <Button variant="ghost" size="sm" onClick={onRemove} data-testid={`section-remove-${section.id}`} aria-label="remove section">
          <Trash2 size={14} className="text-err" />
        </Button>
      </div>

      {(section.items || []).length === 0 ? (
        <div className="text-xs text-ink-muted px-2 py-3 border border-dashed border-white/10 rounded-md text-center">
          {t("menus.no_items_in_section")}
        </div>
      ) : (
        <div className="space-y-1.5">
          {section.items.map((it, iIdx) => (
            <DraggableSectionItem
              key={it.id}
              it={it}
              iIdx={iIdx}
              sectionIdx={idx}
              onItemDragStart={onItemDragStart}
              onItemDragOver={onItemDragOver}
              onItemDrop={onItemDrop}
              onItemChange={onItemChange}
              onItemRemove={onItemRemove}
              onItemReorder={onItemReorder}
            />
          ))}
        </div>
      )}

      <Button variant="ghost" size="sm" onClick={onAddItem} data-testid={`section-add-item-${section.id}`}>
        <Plus size={14} /> {t("menus.add_item")}
      </Button>
    </Card>
  );
}

// ---------- Builder ----------
function MenuBuilder({ open, onClose, initial, onSaved }) {
  const { t } = useTranslation();
  const [data, setData] = useState(initial);
  const [meta, setMeta] = useState({ types: [], statuses: [] });
  const [saving, setSaving] = useState(false);
  const [pickerForSection, setPickerForSection] = useState(null);
  const [dragState, setDragState] = useState(null);  // {sectionIdx, itemIdx}
  const [sectionDrag, setSectionDrag] = useState(null);  // idx

  // Phase 29/30 — Auto-save (30 s) into localStorage with restore banner.
  const draftId = initial?.id || "new";
  const { draftFound, loadDraft, clearDraft, dismissDraft, lastSavedAt } = useAutoSaveDraft({
    scope: "menu-builder", id: draftId, value: data, enabled: open,
  });
  const restoreDraft = () => {
    const v = loadDraft();
    if (v) {
      setData(v);
      toast.success(t("drafts.restored", "Entwurf wiederhergestellt"));
    }
    dismissDraft();
  };
  const discardDraft = () => clearDraft();

  // Phase 30 F9 — unsaved-changes warning on browser navigation
  const isDirty = open && JSON.stringify(data) !== JSON.stringify(initial);
  useUnsavedChanges(isDirty);

  useEffect(() => { setData(initial); }, [initial]);

  useEffect(() => {
    if (!open) return;
    api.get("/menus/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, [open]);

  if (!open || !data) return null;

  const update = (patch) => setData((d) => ({ ...d, ...patch }));

  const addSection = () => {
    update({ sections: [...(data.sections || []), { id: uid(), name: t("menus.new_section"), items: [] }] });
  };
  const updateSection = (idx, sec) => {
    const next = [...data.sections]; next[idx] = sec; update({ sections: next });
  };
  const removeSection = (idx) => {
    if (!window.confirm(t("menus.remove_section_confirm"))) return;
    const next = [...data.sections]; next.splice(idx, 1); update({ sections: next });
  };
  const addItem = (idx, picked) => {
    const next = [...data.sections];
    const sec = next[idx];
    sec.items = [...(sec.items || []), {
      id: uid(),
      kind: picked.kind,
      dish_id: picked.dish_id,
      name: picked.name,
      category: picked.category,
      menu_price: null,
    }];
    update({ sections: next });
  };
  const itemChange = (sIdx, iIdx, it) => {
    const next = [...data.sections];
    next[sIdx].items[iIdx] = it;
    update({ sections: next });
  };
  const itemRemove = (sIdx, iIdx) => {
    const next = [...data.sections];
    next[sIdx].items.splice(iIdx, 1);
    update({ sections: next });
  };

  // Phase 30 F9 — drag & drop reorder
  const itemReorder = (sIdx, fromIdx, toIdx) => {
    const next = [...data.sections];
    const items = [...next[sIdx].items];
    const [moved] = items.splice(fromIdx, 1);
    items.splice(toIdx, 0, moved);
    next[sIdx] = { ...next[sIdx], items };
    update({ sections: next });
  };
  const onSectionDrag = (phase, idx, toIdx) => {
    if (phase === "start") { setSectionDrag(idx); return; }
    if (phase === "drop") {
      if (sectionDrag == null || sectionDrag === idx) { setSectionDrag(null); return; }
      const next = [...data.sections];
      const [moved] = next.splice(sectionDrag, 1);
      next.splice(idx, 0, moved);
      setSectionDrag(null);
      update({ sections: next });
    }
    if (phase === "touch-reorder") {
      // idx = from, toIdx = to (passed by useTouchDraggable)
      if (idx === toIdx || toIdx == null) return;
      const next = [...data.sections];
      const [moved] = next.splice(idx, 1);
      next.splice(toIdx, 0, moved);
      update({ sections: next });
    }
  };

  const handleSave = async () => {
    if (!data.name?.trim()) { toast.error(t("menus.name_required")); return; }
    setSaving(true);
    try {
      const payload = {
        name: data.name.trim(),
        type: data.type,
        status: data.status,
        description: data.description || "",
        show_prices: !!data.show_prices,
        show_allergens: !!data.show_allergens,
        sections: (data.sections || []).map((s) => ({
          id: s.id,
          name: s.name || "",
          type: s.type || "Individuell",
          items: (s.items || []).map((it) => ({
            id: it.id,
            kind: it.kind,
            dish_id: it.dish_id,
            menu_price: it.menu_price == null ? null : Number(it.menu_price),
            note: it.note || "",
          })),
        })),
      };
      const res = data.id
        ? await api.patch(`/menus/${data.id}`, payload)
        : await api.post("/menus", payload);
      toast.success(t("menus.saved"));
      clearDraft();   // Phase 29 — drop the localStorage draft once persisted
      onSaved(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-bg-void/80 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div className="bg-bg-void border-l border-white/10 w-full max-w-3xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="menu-builder">
        <div className="sticky top-0 bg-bg-void border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-ink-muted font-medium">
              {data.id ? t("menus.edit") : t("menus.new")}
            </div>
            <div className="text-lg font-semibold text-ink-primary">
              {data.name || t("menus.new")}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="builder-close">
              <X size={14} /> {t("common.cancel")}
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="builder-save">
              {saving ? <Loader2 size={14} className="animate-spin" /> : t("common.save")}
            </Button>
          </div>
        </div>

        {/* Phase 29 — auto-save draft banner + status */}
        {draftFound && (
          <DraftRestoreBanner draft={draftFound} onRestore={restoreDraft} onDiscard={discardDraft} />
        )}
        {lastSavedAt && (
          <div className="px-6 pt-2 text-[10px] text-ink-muted" data-testid="menu-draft-status">
            {t("drafts.auto_saved_at", "Entwurf zwischengespeichert um {{time}}", {
              time: new Date(lastSavedAt).toLocaleTimeString("de-AT", { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
            })}
          </div>
        )}

        <div className="p-6 space-y-6">
          {/* Basics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="menu-name">{t("menus.name")}</Label>
              <Input id="menu-name" value={data.name || ""} onChange={(e) => update({ name: e.target.value })} placeholder={t("menus.name_ph")} data-testid="builder-name" />
            </div>
            <div>
              <Label htmlFor="menu-type">{t("menus.type")}</Label>
              <Select id="menu-type" value={data.type || "Karte"} onChange={(e) => update({ type: e.target.value })} data-testid="builder-type">
                {meta.types.map((tp) => (<option key={tp} value={tp}>{t(`menus.type_${tp}`, tp)}</option>))}
              </Select>
            </div>
            <div>
              <Label htmlFor="menu-status">{t("menus.status")}</Label>
              <Select id="menu-status" value={data.status || "active"} onChange={(e) => update({ status: e.target.value })} data-testid="builder-status">
                {meta.statuses.map((s) => (<option key={s} value={s}>{t(`menus.status_${s}`, s)}</option>))}
              </Select>
            </div>
            <div className="flex items-end gap-4">
              <label className="flex items-center gap-2 text-sm text-ink-secondary cursor-pointer">
                <input type="checkbox" checked={!!data.show_prices} onChange={(e) => update({ show_prices: e.target.checked })} data-testid="builder-show-prices" />
                {t("menus.show_prices")}
              </label>
              <label className="flex items-center gap-2 text-sm text-ink-secondary cursor-pointer">
                <input type="checkbox" checked={!!data.show_allergens} onChange={(e) => update({ show_allergens: e.target.checked })} data-testid="builder-show-allergens" />
                {t("menus.show_allergens")}
              </label>
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="menu-desc">{t("menus.description")}</Label>
              <textarea
                id="menu-desc"
                value={data.description || ""}
                onChange={(e) => update({ description: e.target.value })}
                className="w-full bg-bg-void border border-white/10 rounded-md px-3 py-2 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
                rows={2}
                placeholder={t("menus.description_ph")}
                data-testid="builder-description"
              />
            </div>
          </div>

          {/* Live totals */}
          {data.totals && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <Kpi label={t("menus.kpi_items")} value={data.totals.items} sub={`${data.totals.recipes}🍽 · ${data.totals.cocktails}🍸`} />
              <Kpi label={t("menus.kpi_revenue")} value={fmtEur(data.totals.total_revenue)} />
              <Kpi label={t("menus.kpi_cost")} value={fmtEur(data.totals.total_cost)} />
              <Kpi label={t("menus.kpi_food_cost")} value={fmtPct(data.totals.avg_food_cost_pct)} tone={tone(data.totals.food_traffic)} />
              <Kpi label={t("menus.kpi_pour_cost")} value={fmtPct(data.totals.avg_pour_cost_pct)} tone={tone(data.totals.pour_traffic)} />
            </div>
          )}

          {/* Sections */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider text-ink-muted font-medium">{t("menus.sections")}</div>
              <Button variant="secondary" size="sm" onClick={addSection} data-testid="builder-add-section">
                <Plus size={14} /> {t("menus.add_section")}
              </Button>
            </div>
            {(data.sections || []).length === 0 ? (
              <div className="text-sm text-ink-muted text-center py-8 border border-dashed border-white/10 rounded-md">
                {t("menus.no_sections")}
              </div>
            ) : (
              data.sections.map((sec, idx) => (
                <SectionCard
                  key={sec.id}
                  section={sec}
                  idx={idx}
                  sectionsCount={data.sections.length}
                  onChange={(s) => updateSection(idx, s)}
                  onRemove={() => removeSection(idx)}
                  onAddItem={() => setPickerForSection(idx)}
                  onItemChange={(iIdx, it) => itemChange(idx, iIdx, it)}
                  onItemRemove={(iIdx) => itemRemove(idx, iIdx)}
                  onItemReorder={(fromIdx, toIdx) => itemReorder(idx, fromIdx, toIdx)}
                  onSectionDrag={onSectionDrag}
                  dragState={dragState}
                  setDragState={setDragState}
                />
              ))
            )}
          </div>
        </div>

        <DishPicker
          open={pickerForSection != null}
          onClose={() => setPickerForSection(null)}
          onPick={(picked) => { addItem(pickerForSection, picked); setPickerForSection(null); }}
        />
      </div>
    </div>
  );
}

// ---------- Main page ----------
export default function MenusPage() {
  const { t } = useTranslation();
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [builderInitial, setBuilderInitial] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/menus");
      setMenus(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleOpenNew = () => {
    setBuilderInitial({
      name: "",
      type: "Karte",
      status: "active",
      description: "",
      show_prices: true,
      show_allergens: true,
      sections: [{ id: uid(), name: "Vorspeisen", items: [] }],
    });
  };
  const handleEdit = async (id) => {
    try {
      const { data } = await api.get(`/menus/${id}`);
      setBuilderInitial(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const handleDelete = async (id, name) => {
    if (!window.confirm(t("menus.delete_confirm", { name }))) return;
    try {
      await api.delete(`/menus/${id}`);
      toast.success(t("menus.deleted"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const handleDownloadPdf = async (id, name) => {
    setDownloading(id);
    try {
      const token = localStorage.getItem("studioops_token");
      const res = await fetch(`${API}/menus/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("PDF failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `speisekarte_${(name || "menu").toLowerCase().replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("menus.pdf_ready"));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDownloading(null); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="menus-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-brand mb-1 flex items-center gap-2">
            <BookText size={12} /> {t("menus.kicker")}
          </div>
          <h1 className="text-2xl font-semibold text-ink-primary">{t("menus.title")}</h1>
          <p className="text-sm text-ink-secondary mt-1 max-w-xl">{t("menus.subtitle")}</p>
        </div>
        <Button onClick={handleOpenNew} data-testid="menu-new-btn">
          <Plus size={14} /> {t("menus.add")}
        </Button>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Spinner size={24} /></div>
      ) : menus.length === 0 ? (
        <EmptyState
          icon={BookText}
          title={t("menus.empty_title")}
          description={t("menus.empty_subtitle")}
          action={<Button onClick={handleOpenNew} data-testid="menu-empty-cta"><Plus size={14} /> {t("menus.add")}</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="menus-grid">
          {menus.map((m) => (
            <Card key={m.id} className="p-4 hover:border-brand/30 transition-all" data-testid={`menu-card-${m.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge tone="brand">{t(`menus.type_${m.type}`, m.type)}</Badge>
                    <Badge tone={m.status === "active" ? "ok" : "default"}>{t(`menus.status_${m.status}`, m.status)}</Badge>
                  </div>
                  <div className="text-base font-semibold text-ink-primary truncate">{m.name}</div>
                  {m.description && <div className="text-xs text-ink-muted truncate mt-0.5">{m.description}</div>}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 mt-3 mb-3">
                <div className="bg-bg-void rounded-md px-2 py-1.5 text-center">
                  <div className="text-[9px] uppercase tracking-wider text-ink-muted">{t("menus.kpi_items_short")}</div>
                  <div className="text-sm font-medium num">{m.totals.items}</div>
                </div>
                <div className="bg-bg-void rounded-md px-2 py-1.5 text-center">
                  <div className="text-[9px] uppercase tracking-wider text-ink-muted">{t("menus.kpi_revenue_short")}</div>
                  <div className="text-sm font-medium num">{fmtEur(m.totals.total_revenue)}</div>
                </div>
                <div className={`rounded-md px-2 py-1.5 text-center ${m.totals.food_traffic === "red" ? "bg-err/10" : m.totals.food_traffic === "yellow" ? "bg-warn/10" : "bg-bg-void"}`}>
                  <div className="text-[9px] uppercase tracking-wider text-ink-muted">{t("menus.kpi_food_cost_short")}</div>
                  <div className="text-sm font-medium num">{fmtPct(m.totals.avg_food_cost_pct)}</div>
                </div>
              </div>

              {m.allergen_union && m.allergen_union.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {m.allergen_union.slice(0, 6).map((a) => (
                    <Badge key={a} tone="warn" className="text-[10px]">{t(`allergens.${a}`, a)}</Badge>
                  ))}
                  {m.allergen_union.length > 6 && <Badge tone="warn" className="text-[10px]">+{m.allergen_union.length - 6}</Badge>}
                </div>
              )}

              <div className="flex items-center gap-1.5 pt-2 border-t border-white/5">
                <Button variant="ghost" size="sm" onClick={() => handleEdit(m.id)} data-testid={`menu-edit-${m.id}`}>
                  <Edit3 size={12} /> {t("common.edit")}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDownloadPdf(m.id, m.name)} disabled={downloading === m.id} data-testid={`menu-pdf-${m.id}`}>
                  {downloading === m.id ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />}
                  PDF
                </Button>
                <div className="flex-1" />
                <Button variant="ghost" size="sm" onClick={() => handleDelete(m.id, m.name)} data-testid={`menu-delete-${m.id}`}>
                  <Trash2 size={12} className="text-err" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <MenuBuilder
        open={builderInitial != null}
        onClose={() => setBuilderInitial(null)}
        initial={builderInitial}
        onSaved={(saved) => { setBuilderInitial(saved); load(); }}
      />
    </div>
  );
}
